<template>
  <section id="home" class="flex-full">
    <home-category />
    <instruction-redirect v-if="getCurrentDevice && getCurrentDevice !== 'desktop'" :device="getCurrentDevice" />
    <instagram-feed />
    <description />
  </section>
</template>

<script>
// query constructor
import { onlineHelper, isServer } from '@vue-storefront/core/helpers'

// Core pages
import Home from '@vue-storefront/core/pages/Home'

import { mapGetters } from 'vuex'
import HomeCategory from 'theme/components/core/blocks/Switcher/HomeCategory'

import i18n from '@vue-storefront/i18n';
import config from 'config'
const InstagramFeed = () => import('theme/components/theme/blocks/InstagramFeed/InstagramFeed')
const Description = () => import('theme/components/theme/blocks/Home/Description')
const InstructionRedirect = () => import('theme/components/core/InstructionRedirect')

export default {
  mixins: [Home],
  components: {
    Description,
    InstagramFeed,
    HomeCategory,
    InstructionRedirect
  },
  // SSR only; on the client HomeCategory/HomeStore load in mounted(). The categories
  // layout renders category cards only, so it needs no partner list.
  async asyncData ({ store, route }) {
    if (!isServer) {
      return
    }
    const locationCode = route.params.locationCode
    const activeLocation = store.getters['locations/getActiveLocation']
    if (activeLocation && activeLocation.layout_code !== 'partners_only') {
      await store.dispatch('partner_categories/ensureCategories', { locationCode: activeLocation.code })
    } else if (locationCode) {
      await store.dispatch('partners/loadPartners', { locationCode })
    }
  },
  computed: {
    ...mapGetters('user', ['isLoggedIn']),
    ...mapGetters({
      currentLocation: 'locations/getActiveLocation',
      metaInformation: 'configs/getMetaInformation',
      selectedTags: 'partner_tags/getSelectedTags',
      getCurrentDevice: 'ui/getDevice'
    }),

    getStandaloneStatus () {
      return window.matchMedia('(display-mode: standalone)').matches;
    },

    isOnline () {
      return onlineHelper.isOnline
    },

    metaTitle () {
      return this.metaInformation.find(meta => meta.key === 'title').value
    },

    metaDescription () {
      return this.metaInformation.find(meta => meta.key === 'description').value
    },

    metaKeywords () {
      return this.metaInformation.find(meta => meta.key === 'keywords').value
    },

    ogMetaType () {
      if (this.metaInformation.find(meta => meta.key === 'og_type')) {
        return this.metaInformation.find(meta => meta.key === 'og_type').value
      }
      return ''
    },

    ogMetaTitle () {
      if (this.metaInformation.find(meta => meta.key === 'og_title')) {
        return this.metaInformation.find(meta => meta.key === 'og_title').value
      }
      return ''
    },

    ogMetaDescription () {
      if (this.metaInformation.find(meta => meta.key === 'og_description')) {
        return this.metaInformation.find(meta => meta.key === 'og_description').value
      }
      return ''
    },

    ogMetaImage () {
      if (this.metaInformation.find(meta => meta.key === 'og_image')) {
        return this.metaInformation.find(meta => meta.key === 'og_image').value
      }
      return ''
    },
    getHostPath () {
      return config.canonical ? config.canonical.url : ''
    }
  },
  created () {
    // Load personal and shipping details for Checkout page from IndexedDB
    this.$store.dispatch('checkout/load')
  },
  mounted () {
    if (localStorage.getItem('redirect')) {
      if (this.isLoggedIn) return localStorage.removeItem('redirect');
      this.$bus.$emit('modal-show', 'modal-signup')
    }
  },
  metaInfo () {
    // if tags are selected
    if (this.selectedTags.length > 0 && this.selectedTags[0].meta) {
      let locationMeta = {}
      this.selectedTags[0].meta.map(meta => {
        if (meta.location_code === this.currentLocation.code) {
          locationMeta = meta
        }
      })
      let currentLocationMeta = [
        {
          vmid: 'description',
          name: 'description',
          content: locationMeta.meta_description ? locationMeta.meta_description : this.metaDescription
        },
        {
          vmid: 'keywords',
          name: 'keywords',
          content: locationMeta.meta_keywords ? locationMeta.meta_keywords : this.metaKeywords
        },
        {
          vmid: 'og:type',
          property: 'og:type',
          content: locationMeta.og_meta_type ? locationMeta.og_meta_type : this.ogMetaType
        },
        {
          vmid: 'og:title',
          property: 'og:title',
          content: locationMeta.og_meta_title ? locationMeta.og_meta_title : this.ogMetaTitle
        },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: locationMeta.og_meta_description ? locationMeta.og_meta_description : this.ogMetaDescription
        },
        {
          vmid: 'og:image',
          property: 'og:image',
          content: locationMeta.og_meta_image ? locationMeta.og_meta_image : this.ogMetaImage
        }
      ]

      return {
        title: locationMeta.meta_title,
        meta: currentLocationMeta,
        link: [
          { rel: 'canonical', href: this.getHostPath + this.$route.fullPath }
        ]
      }
    }

    // if location is chosen
    if (this.currentLocation) {
      let currentLocationMeta = [
        {
          vmid: 'description',
          name: 'description',
          content: this.currentLocation.meta_description ? this.currentLocation.meta_description : this.metaDescription
        },
        {
          vmid: 'keywords',
          name: 'keywords',
          content: this.currentLocation.meta_keywords ? this.currentLocation.meta_keywords : this.metaKeywords
        },
        {
          vmid: 'og:type',
          property: 'og:type',
          content: this.currentLocation.og_meta_type ? this.currentLocation.og_meta_type : this.ogMetaType
        },
        {
          vmid: 'og:title',
          property: 'og:title',
          content: this.currentLocation.og_meta_title ? this.currentLocation.og_meta_title : this.ogMetaTitle
        },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: this.currentLocation.og_meta_description ? this.currentLocation.og_meta_description : this.ogMetaDescription
        },
        {
          vmid: 'og:image',
          property: 'og:image',
          content: this.currentLocation.og_meta_image ? this.currentLocation.og_meta_image : this.ogMetaImage
        },
        {
          vmid: 'og:url',
          property: 'og:url',
          content: this.getHostPath + this.$route.fullPath
        }
      ]

      return {
        title: this.currentLocation.meta_title,
        meta: currentLocationMeta,
        link: [
          { rel: 'canonical', href: this.getHostPath + this.$route.fullPath }
        ]
      }
    }

    // otherwise (location and tags are not selected)
    return {
      title: this.metaTitle || i18n.t('Доставка до 60 хв з меню закладів харчування та супермаркетів.'),
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content: this.metaDescription || 'Привеземо Вам усе що забажаєте. У нас в каталозі Ви можете переглянути та замовити страви та товари з найкращих закладів харчування, супермаркетів та гіпермаркетів міста'
        },
        {
          vmid: 'keywords',
          name: 'keywords',
          content: this.metaKeywords
        },
        {
          vmid: 'og:type',
          property: 'og:type',
          content: this.ogMetaType
        },
        {
          vmid: 'og:title',
          property: 'og:title',
          content: this.ogMetaTitle
        },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: this.ogMetaDescription
        },
        {
          vmid: 'og:image',
          property: 'og:image',
          content: this.ogMetaImage
        }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
    #home{
        position: relative;
    }
    .new-collection {
        @media (max-width: 768px) {
            padding-top: 0;
        }
    }
    .menu-bottom {
        display: block;
        margin-top: -50px;
    }
</style>
